import { MutableRefObject, useEffect, useRef } from 'react';

export interface Refinement<T> {
  /**
   * Refinement function signature
   */
  (data: T): boolean | Promise<boolean>;

  /**
   * Method to invalidate the refinement and allow re-performing
   */
  invalidate(): void;
}

export interface RefinementCallback<T> {
  (data: T): boolean | Promise<boolean>; // Callback function signature
}

/**
 * Special hook returning a callback to be used in conjunction with zod refinements.
 * When a zod schema is used within a form, it will run on every change, calling all refinements
 * regardless of whether the actual data used inside the refinement has changed or not.
 *
 * This hook returns a special refinement function that will:
 * - run the callback only once (caching the result),
 * - debounce the callback if needed,
 * - and allow invalidation by calling `invalidate` on the function.
 *
 * @param callback The refinement callback
 * @param options { debounce?: number } Optional debounce time in milliseconds
 * @returns A refinement function that can be used in zod schemas. Has an `invalidate` method.
 */
export const useRefinement = <T,>(
  callback: RefinementCallback<T>,
  { debounce }: { debounce?: number } = {},
): Refinement<T> => {
  const ctxRef = useRef<RefinementContext<T>>({ callback, debounce });
  const refinementRef = useRef(createRefinement(ctxRef));

  ctxRef.current = { callback, debounce };

  // On component unmount, abort any ongoing refinements
  useEffect(() => () => refinementRef.current.abort(), []);

  return refinementRef.current.refine;
};

interface RefinementContext<T> {
  callback: RefinementCallback<T>;
  debounce?: number;
}

const createRefinement = <T,>(
  ctxRef: MutableRefObject<RefinementContext<T>>,
) => {
  let result: Promise<boolean> | null = null;
  let timeout: ReturnType<typeof setTimeout> | null = null;

  const start = async (data: T) => {
    const { debounce, callback } = ctxRef.current;

    if (debounce !== undefined) {
      await new Promise((resolve) => {
        timeout = setTimeout(resolve, debounce);
      });
    }

    return callback(data);
  };

  const refine: Refinement<T> = async (data: T) => {
    if (result) {
      return result;
    }
    return (result = start(data));
  };

  const abort = () => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = null;
  };

  refine.invalidate = () => {
    // Cancel ongoing work and reset cached result
    abort();
    result = null;
  };

  return { refine, abort };
};
